import { useState, useEffect, useCallback } from "react";
import { AlertTriangle, Info, Shield } from "lucide-react";
import { CollapsibleSection } from "./CollapsibleSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BonferroniAdvisory({ projectId, token, currentAlpha, refreshKey }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/projects/${projectId}/hypothesis-test-history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setData(await res.json());
    } catch (e) {
      console.error("Failed to load hypothesis test history:", e);
    }
  }, [projectId, token]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (!data || data.total_tests < 2) return null;

  const { total_tests, base_alpha, bonferroni_alpha, reclassified, advisory } = data;
  const hasReclassified = reclassified?.length > 0;

  return (
    <div
      className={`p-4 rounded-lg border ${hasReclassified ? "bg-amber-500/10 border-amber-500/30" : "bg-primary/5 border-primary/20"}`}
      data-testid="bonferroni-advisory"
    >
      <div className="flex items-start gap-3">
        {hasReclassified ? (
          <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
        ) : (
          <Shield className="w-5 h-5 text-primary shrink-0 mt-0.5" />
        )}
        <div className="flex-1 min-w-0">
          <h4 className={`font-medium text-sm ${hasReclassified ? "text-amber-400" : "text-primary"}`} data-testid="bonferroni-title">
            Bonferroni Correction Advisory
          </h4>
          <p className="text-xs text-muted-foreground mt-1" data-testid="bonferroni-description">
            {advisory}
          </p>

          <div className="mt-3 grid grid-cols-3 gap-3">
            <div className="bg-background/50 rounded p-2">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Tests Run</p>
              <p className="text-sm font-bold font-mono" data-testid="bonferroni-test-count">{total_tests}</p>
            </div>
            <div className="bg-background/50 rounded p-2">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Original Alpha</p>
              <p className="text-sm font-bold font-mono">{base_alpha}</p>
            </div>
            <div className="bg-background/50 rounded p-2">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Corrected Alpha</p>
              <p className={`text-sm font-bold font-mono ${hasReclassified ? "text-amber-400" : "text-primary"}`} data-testid="bonferroni-corrected-alpha">{bonferroni_alpha}</p>
            </div>
          </div>

          {hasReclassified && (
            <CollapsibleSection
              title={
                <span className="text-xs text-amber-400">
                  {reclassified.length} test(s) would lose significance
                </span>
              }
              icon={<Info className="w-3.5 h-3.5 text-amber-400" />}
              open={open}
              onToggle={() => setOpen(v => !v)}
              testId="bonferroni-reclassified"
            >
              <div className="space-y-2 mt-2">
                {reclassified.map((r, i) => (
                  <div key={i} className="p-2 bg-background border border-border rounded text-xs">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{r.test_name || `${r.column_a} vs ${r.column_b}`}</span>
                      <span className="text-amber-400 font-mono">p = {typeof r.p_value === 'number' ? r.p_value.toFixed(4) : r.p_value}</span>
                    </div>
                    <p className="text-muted-foreground mt-0.5">
                      Significant at alpha = {r.original_alpha}, but <strong className="text-amber-400">not significant</strong> at Bonferroni-corrected alpha = {typeof r.bonferroni_alpha === 'number' ? r.bonferroni_alpha.toFixed(4) : r.bonferroni_alpha}
                    </p>
                  </div>
                ))}
              </div>
            </CollapsibleSection>
          )}
        </div>
      </div>
    </div>
  );
}
